import React, { useCallback } from 'react';
import { pdf, Font } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

import { formatCompany, formatJob } from './helpers';
import { FONTS } from './constants';
import Render from './Render';
import type { Company, Job } from '~/hooks/useQuoteDownload/types';

if (process.env.NEXT_PUBLIC_CUSTOM_FONTS !== 'false') {
  Font.register({
    family: 'Galano Grotesque',
    src: FONTS.Galano,
  });

  Font.register({
    family: 'Galano Grotesque Bold',
    src: FONTS.GalanoBold,
  });
}

export interface QuoteDownloadHook {
  createPdf: (job: Job, company: Company) => void;
  createBase64: (job: Job, company: Company) => void;
}

export default function useQuoteDownload(): QuoteDownloadHook {
  const generatePdf = async (job: Job, company: Company): Promise<Blob> => {
    // Format job data
    const formattedJob = formatJob(job);
    // Format company data
    const formattedCompany = formatCompany(company);

    const input = {
      ...formattedJob,
      ...formattedCompany,
    } as unknown as Job;

    // Create PDF blob
    const generated = await pdf(<Render job={input} />);
    const blob = await generated.toBlob();
    return blob;
  };

  const createPdf = useCallback(
    async (job: Job, company: Company): Promise<void> => {
      // Construct filename
      const fileName = `kanda-job${job?.id ? `-${job.id}` : ''}.pdf`;
      const blob = await generatePdf(job, company);

      // // Save the PDF
      saveAs(blob, fileName);
    },
    [],
  );

  const toBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

  const createBase64 = async (
    job: Job,
    company: Company,
  ): Promise<string | ArrayBuffer | null> => {
    const blob = await generatePdf(job, company);
    const base64 = await toBase64(blob);
    return base64;
  };

  return {
    createPdf,
    createBase64,
  };
}
